import { StateCreator } from 'zustand'
import { Notification } from '@types'

export interface UISlice {
  ui: {
    loading: boolean
    sidebarOpen: boolean
    language: string
    notifications: Notification[]
    modal: {
      isOpen: boolean
      type: string | null
      data: any
    }
    toast: {
      isVisible: boolean
      message: string
      type: 'success' | 'error' | 'warning' | 'info'
    }
    scrollProgress: number
    setLoading: (loading: boolean) => void
    toggleSidebar: () => void
    setLanguage: (language: string) => void
    addNotification: (notification: Omit<Notification, 'id'>) => void
    removeNotification: (id: string) => void
    clearNotifications: () => void
    openModal: (type: string, data?: any) => void
    closeModal: () => void
    showToast: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void
    hideToast: () => void
    setScrollProgress: (progress: number) => void
    scrollToTop: () => void
  }
}

export const createUISlice: StateCreator<UISlice> = (set, get) => ({
  ui: {
    loading: false,
    sidebarOpen: false,
    language: 'es',
    notifications: [],
    modal: {
      isOpen: false,
      type: null,
      data: null,
    },
    toast: {
      isVisible: false,
      message: '',
      type: 'info',
    },
    scrollProgress: 0,
    
    setLoading: (loading: boolean) => {
      set((state) => ({
        ui: {
          ...state.ui,
          loading,
        },
      }))
    },
    
    toggleSidebar: () => {
      set((state) => ({
        ui: {
          ...state.ui,
          sidebarOpen: !state.ui.sidebarOpen,
        },
      }))
    },
    
    setLanguage: (language: string) => {
      set((state) => ({
        ui: {
          ...state.ui,
          language,
        },
      }))
    },
    
    addNotification: (notification) => {
      const id = Math.random().toString(36).substr(2, 9)
      const newNotification: Notification = {
        ...notification,
        id,
        timestamp: new Date().toISOString(),
      }
      
      set((state) => ({
        ui: {
          ...state.ui,
          notifications: [...state.ui.notifications, newNotification],
        },
      }))
      
      // Auto remove notification after duration
      if (notification.duration) {
        setTimeout(() => {
          get().ui.removeNotification(id)
        }, notification.duration)
      }
    },
    
    removeNotification: (id: string) => {
      set((state) => ({
        ui: {
          ...state.ui,
          notifications: state.ui.notifications.filter((n) => n.id !== id),
        },
      }))
    },
    
    clearNotifications: () => {
      set((state) => ({
        ui: {
          ...state.ui,
          notifications: [],
        },
      }))
    },
    
    openModal: (type: string, data?: any) => {
      set((state) => ({
        ui: {
          ...state.ui,
          modal: {
            isOpen: true,
            type,
            data,
          },
        },
      }))
    },
    
    closeModal: () => {
      set((state) => ({
        ui: {
          ...state.ui,
          modal: {
            isOpen: false,
            type: null,
            data: null,
          },
        },
      }))
    },
    
    showToast: (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
      set((state) => ({
        ui: {
          ...state.ui,
          toast: {
            isVisible: true,
            message,
            type,
          },
        },
      }))
      
      // Auto hide toast after 4 seconds
      setTimeout(() => {
        get().ui.hideToast()
      }, 4000)
    },
    
    hideToast: () => {
      set((state) => ({
        ui: {
          ...state.ui,
          toast: {
            isVisible: false,
            message: '',
            type: 'info',
          },
        },
      }))
    },
    
    setScrollProgress: (progress: number) => {
      set((state) => ({
        ui: {
          ...state.ui,
          scrollProgress: progress,
        },
      }))
    },
    
    scrollToTop: () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    },
  },
}) 